/* @flow */
import { Container } from 'flux/utils';

import BoxBody from 'components/BoxBody.jsx';
import BoxFooter from 'components/BoxFooter.jsx';
import Button from 'components/Button.jsx';
import CurrentUser from 'auth/CurrentUser';
import DialogContent from 'components/DialogContent.jsx';
import EventTracking from 'lib/EventTracking.js';
import Field from 'components/Field.jsx';
import GroupActions from 'groups/actions/GroupActions';
import GroupStore from 'groups/stores/GroupStore';
import Image from 'components/Image.jsx';
import Label from 'components/Label.jsx';
import Lead from 'entities/Lead';
import LeadActions from 'actions/LeadActions';
import LeadStore from 'stores/LeadStore';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import { NO_SCHOOL } from 'constants/OrganizationConstants';
import Organization from 'entities/Organization';
import OrganizationStore from 'stores/OrganizationStore';
import Organizations from 'collections/Organizations';
import React from 'react';
import SchoolPicker from 'components/SchoolPicker.jsx';
import ToastActions from 'actions/ToastActions';
import UserActions from 'user/UserActions';
import OrganizationActions from 'actions/OrganizationActions';

const EVENT_SCREEN_NAME = 'SetupSchool';

import './styles/SetupSchool.scss';

type Props = {};

type State = {
  lead: ?Lead,
  orgForLead: ?Organization,
  suggesting: boolean,
  schoolId: ?number,
  submitting: boolean,
  suggestedSchools: ?Organizations,
};

class SetupSchool extends React.Component {
  state: State;

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  static childContextTypes = {
    eventScreenName: React.PropTypes.string,
  };

  static getStores() {
    return [LeadStore, OrganizationStore];
  }

  static calculateState(prevState) {
    const lead = LeadStore.getLeadForUser(CurrentUser.getUser().id);
    return {
      lead,
      orgForLead: lead && OrganizationStore.getOrganization(lead.getOrganizationId()),
      suggesting: prevState ? prevState.suggesting : true,
      schoolId: prevState && prevState.schoolId,
      submitting: prevState ? prevState.submitting : false,
      suggestedSchools: prevState && prevState.suggestedSchools,
    };
  }

  getChildContext() {
    return {
      eventScreenName: EVENT_SCREEN_NAME,
    };
  }

  componentWillMount() {
    Organizations.suggest().then((orgs) => {
      if (orgs && orgs.length) {
        this.setState({ suggestedSchools: orgs });
      }
    }).catch(error => console.error('SetupSchool - can‘t get organization suggestions', error));

    LeadActions.loadLead(CurrentUser.getUser().id).then(() => {
      const lead = LeadStore.getLeadForUser(CurrentUser.getUser().id);
      this.setState({ lead });
      OrganizationActions.getOrLoadOrganization(lead.getOrganizationId()).then((orgForLead: Organization) => {
        this.setState({ orgForLead });
      }, () => {
        this.setState({ suggesting: false });
      });
    }, () => {
      this.setState({ suggesting: false });
    });
  }

  componentDidMount() {
    EventTracking.view(EVENT_SCREEN_NAME, { user_id: CurrentUser.getUserId() });
  }

  componentWillUpdate(nextProps: Props, nextState: State) {
    if (this.state.suggesting && nextState.lead && (nextState.lead.length === 0)) {
      this.setState({ suggesting: false });
    }
  }

  onSchoolCreate = (organization: Organization) => {
    this.setState({ schoolId: organization.id });
  };

  getFirstOwnedClass() {
    return GroupStore.getOwnedGroups().first();
  }

  didCreateChildClass = () => {
    return this.getFirstOwnedClass() && this.getFirstOwnedClass().hasChildren();
  };

  handleDontWorkClick = () => {
    UserActions.savePreference('works_at_a_school', false)
      .then(LeadActions.deleteLead)
      .then(() => this.updateUserOrganization(NO_SCHOOL.id))
      .catch(ToastActions.showDefaultErrorToast);
  };

  handleNoButtonClick = () => {
    this.setState({ suggesting: false });
  }

  handleSaveSchoolButtonClick = () => {
    if (this.state.schoolId === NO_SCHOOL.id) {
      this.handleDontWorkClick();
    } else {
      this.updateUserOrganization(this.state.schoolId);
    }
  };

  handleYesButtonClick = () => {
    this.updateUserOrganization(this.state.lead ? this.state.lead.getOrganizationId() : null);
  };

  redirectToApp = () => {
    this.context.router.push('/');
  };

  updateUserOrganization = (organizationId: ?number) => {
    const onFailure = (error) => {
      this.setState({ submitting: false });
      ToastActions.showAPIError(error);
    };

    const onSuccess = () => {
      LeadActions.loadLeads(true);
      OrganizationActions.loadUserOrganizations();
      ToastActions.showSuccessToast('Saved!');
      this.redirectToApp();
    };

    const updateUserOrganization = () => {
      UserActions.update({ organization_id: organizationId })
        .then(onSuccess, onFailure);
    };

    const defaultFirstClass = () => {
      const attrs = { organization_id: organizationId };
      GroupActions.patch(this.getFirstOwnedClass(), attrs)
        .then(updateUserOrganization, onFailure);
    };

    this.setState({ submitting: true });
    if (this.getFirstOwnedClass()) {
      defaultFirstClass();
    } else {
      updateUserOrganization();
    }
  }

  renderDontWorkInASchoolButton() {
    return (
      <BoxFooter className="SetupSchool-footer">
        <Button
          eventElementName="dontWorkButton"
          className="SetupSchool-dontWorkButton"
          level="text"
          size="small"
          onClick={this.handleDontWorkClick}>
          I don’t work at a school
        </Button>
      </BoxFooter>
    );
  }

  renderSchoolSelector() {
    return (
      <DialogContent
        className="SetupSchool"
        title="Add your school"
        full
        subtext={
          'Connect with colleagues and help students ' +
          'and parents find your classes.'
        }
        size="small">
        <BoxBody divided>
          <Field className="SetupGroup-field">
            <Label>School</Label>
            <SchoolPicker
              schoolId={this.state.schoolId}
              suggestions={this.state.suggestedSchools}
              placeholder="Search for a school"
              onSelect={value => this.setState({ schoolId: value })}
              onClear={() => this.setState({ schoolId: null })}
              onCreateOrganization={this.onSchoolCreate}
              onNoSchool={this.handleDontWorkClick}
              noQuery="Suggested schools for you"
              autofocus
            />
          </Field>
          <Button
            className="SetupSchool-saveSchoolButton"
            eventElementName="saveSchoolButton"
            disabled={!this.state.schoolId || this.state.submitting}
            onClick={this.handleSaveSchoolButtonClick}>
            {this.state.submitting ?
              <LoadingIndicator className="SetupSchool-saveSchoolButton-loader" /> :
              'Save'}
          </Button>
        </BoxBody>
        {!this.didCreateChildClass() && this.renderDontWorkInASchoolButton()}
      </DialogContent>
    );
  }

  renderSchoolSuggestor() {
    const { lead, orgForLead } = this.state;
    if (!lead || !orgForLead) {
      return (
        <div className="SetupSchool-loading">
          <LoadingIndicator />
        </div>
      );
    }

    return (
      <DialogContent className="SetupSchool" size="small">
        <BoxBody
          className="SetupSchool-content"
          divided
          dividerPlacement="bottom">
          <Image
            className="SetupSchool-image"
            src={require('assets/images/school.svg')}
            alt="School"
          />
          <div className="SetupSchool-title">
            Do you teach at{' '}
            <div className="SetupSchool-school">
              {orgForLead.getName()}?
            </div>
          </div>
          <div className="SetupSchool-buttons">
            <Button
              onClick={this.handleNoButtonClick}
              eventElementName="declineSuggestedSchoolButton">
              No
            </Button>
            <Button
              onClick={this.handleYesButtonClick}
              eventElementName="acceptSuggestedSchoolButton">
              Yes
            </Button>
          </div>
        </BoxBody>
        {!this.didCreateChildClass() && this.renderDontWorkInASchoolButton()}
      </DialogContent>
    );
  }

  render() {
    return this.state.suggesting ? this.renderSchoolSuggestor() : this.renderSchoolSelector();
  }
}

export default Container.create(SetupSchool, {
  pure: false,
});



// WEBPACK FOOTER //
// ./src/setup/components/SetupSchool.jsx